import React, { useState, useCallback, useRef, RefObject, useEffect } from "react";

// dls
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";

// types
import { CouponMessageCard } from "@swiggy-private/cart-types";

// components
import {
    AriaHidden,
    DialogHOC,
    LottieAnimation,
    ScreenReaderOnly,
    getActiveElement,
    isEmpty,
    useEffectOnce,
    useFocusDialog,
} from "@swiggy-private/core-ui";

// ui widgets
import { CloseIcon } from "@cart-ui/widgets/close-icon";
import PlusIcon from "@cart-ui/widgets/plus-icon";

// utils
import { getAriaLabel } from "./utils";

// styles
import {
    CloseIconContainer,
    Container,
    LottieContainer,
    ContentWrapper,
    Heading,
    SubHeading,
    TopLeftDesignElement,
    BottomRightDesignElement,
} from "./styles";

// constants
import {
    CLOSE_ICON,
    PLUS_ICON,
    MAX_SAVINGS_LOTTIE_CONTENT_ANIMATION_DELAY_IN_FRAMES,
    TOP_LEFT_DESIGN_ELEMENT_PROPS,
    BOTTOM_RIGHT_DESIGN_ELEMENT_PROPS,
    TEXTS,
    MAX_SAVINGS_LOTTIE_URL,
} from "./constants";

// test-ids
import { CART_MODAL_MAX_SAVINGS_TEST_IDS } from "@swiggy-private/cart-test-ids";

interface CartModalMaxSavingsProps {
    data: CouponMessageCard;
    closeModal: VoidFunction;
    onClickCouponMaxSavingsPopup?: (text: string) => void;
}

const CartModalMaxSavings: React.FC<CartModalMaxSavingsProps> = ({
    data,
    closeModal,
    onClickCouponMaxSavingsPopup,
}) => {
    const headerDivRef = useRef<HTMLElement | null>(null);
    const previouslyFocusedElement = useRef<HTMLElement | null>(null);
    const textRef = useRef<HTMLHeadingElement>(null);
    const resizeObserverRef = useRef<ResizeObserver | null>(null);
    const [showContent, setShowContent] = useState(false);
    const [isTextWrapped, setIsTextWrapped] = useState(false);

    const { primarySavingsInfo, secondarySavingsInfo, cta } = data;
    const { text: primaryText, subtext: primarySubtext } = primarySavingsInfo || {};
    const { text: secondaryText, subtext: secondarySubtext } = secondarySavingsInfo || {};
    const { text: ctaText = TEXTS.CTA_TEXT } = cta || {};

    const secondaryCalloutsExist = !isEmpty(secondaryText);
    const subtitleVariant = secondaryCalloutsExist ? "V2.overline.neutral.regular" : "V2.body2.neutral.regular";

    const cartModalAriaLabel = getAriaLabel(primaryText, primarySubtext, secondaryText, secondarySubtext);

    const handleFrameEventCompletion = useCallback(() => {
        setShowContent(true);
    }, []);

    const handleClickCouponMaxSavingsPopup = useCallback(() => {
        onClickCouponMaxSavingsPopup?.(cartModalAriaLabel);
        closeModal();
    }, [cartModalAriaLabel, onClickCouponMaxSavingsPopup, closeModal]);

    useFocusDialog(headerDivRef, previouslyFocusedElement);

    useEffectOnce(() => {
        previouslyFocusedElement.current = getActiveElement();
    });

    useEffect(() => {
        if (!textRef.current || isEmpty(primaryText)) return;

        if (!resizeObserverRef.current) {
            resizeObserverRef.current = new ResizeObserver(entries => {
                for (const entry of entries) {
                    const { height } = entry.contentRect;
                    setIsTextWrapped(height > 26);
                }
            });
            resizeObserverRef.current?.observe(textRef.current);
        }

        return () => {
            resizeObserverRef.current?.disconnect();
        };
    }, [primaryText]);

    return (
        <Container
            data-testid={CART_MODAL_MAX_SAVINGS_TEST_IDS.CART_MODAL_MAX_SAVINGS_CONTAINER}
            $secondaryCalloutsExist={secondaryCalloutsExist}
            $primaryTextIsLong={isTextWrapped}
        >
            <LottieContainer
                data-testid={CART_MODAL_MAX_SAVINGS_TEST_IDS.CART_MODAL_MAX_SAVINGS_LOTTIE_CONTAINER}
                $secondaryCalloutsExist={secondaryCalloutsExist}
            >
                <LottieAnimation
                    fileName={MAX_SAVINGS_LOTTIE_URL}
                    loop={false}
                    autoplay={true}
                    backwardSpeed={1}
                    forwardSpeed={1}
                    playIfVisible={true}
                    visible={true}
                    playLottie={true}
                    enableFrameEvent={!showContent}
                    frameEventBreak={MAX_SAVINGS_LOTTIE_CONTENT_ANIMATION_DELAY_IN_FRAMES}
                    onFrameEventCompletion={handleFrameEventCompletion}
                />
            </LottieContainer>
            <TopLeftDesignElement {...TOP_LEFT_DESIGN_ELEMENT_PROPS} startAnimation={showContent} />
            <BottomRightDesignElement {...BOTTOM_RIGHT_DESIGN_ELEMENT_PROPS} startAnimation={showContent} />
            <ScreenReaderOnly
                data-testid={CART_MODAL_MAX_SAVINGS_TEST_IDS.CART_MODAL_MAX_SAVINGS_SCREEN_READER_TEXT}
                text={cartModalAriaLabel}
                as="div"
                compRef={headerDivRef as RefObject<HTMLDivElement>}
            />
            <ContentWrapper $show={showContent} $secondaryCalloutsExist={secondaryCalloutsExist}>
                <Heading
                    ref={textRef}
                    data-testid={CART_MODAL_MAX_SAVINGS_TEST_IDS.CART_MODAL_MAX_SAVINGS_PRIMARY_TEXT}
                    variant="V2.heading1.neutral.black"
                    color="text_High_Emphasis"
                    maxLines={2}
                >
                    {primaryText}
                </Heading>
                <SubHeading
                    data-testid={CART_MODAL_MAX_SAVINGS_TEST_IDS.CART_MODAL_MAX_SAVINGS_PRIMARY_SUBTEXT}
                    variant={subtitleVariant}
                    color="text_Low_Emphasis"
                    maxLines={1}
                    addExtraMarginBottom={!secondaryCalloutsExist}
                >
                    {primarySubtext}
                </SubHeading>
                {secondaryCalloutsExist && (
                    <AriaHidden
                        data-testid={CART_MODAL_MAX_SAVINGS_TEST_IDS.CART_MODAL_MAX_SAVINGS_PLUS_ICON}
                        as="span"
                    >
                        <PlusIcon iconFillColor={PLUS_ICON.fill} size={PLUS_ICON.size} />
                    </AriaHidden>
                )}
                {!isEmpty(secondaryText) && (
                    <Heading
                        data-testid={CART_MODAL_MAX_SAVINGS_TEST_IDS.CART_MODAL_MAX_SAVINGS_SECONDARY_TEXT}
                        variant="V2.heading1.neutral.black"
                        color="text_High_Emphasis"
                        maxLines={1}
                    >
                        {secondaryText}
                    </Heading>
                )}
                {!isEmpty(secondarySubtext) && (
                    <SubHeading
                        data-testid={CART_MODAL_MAX_SAVINGS_TEST_IDS.CART_MODAL_MAX_SAVINGS_SECONDARY_SUBTEXT}
                        variant={subtitleVariant}
                        color="text_Low_Emphasis"
                        maxLines={1}
                        addExtraMarginBottom={true}
                    >
                        {secondarySubtext}
                    </SubHeading>
                )}
                <SuperButton
                    data-testid={CART_MODAL_MAX_SAVINGS_TEST_IDS.CART_MODAL_MAX_SAVINGS_CTA_TEXT}
                    variant="fullwidth"
                    buttonType="primary"
                    buttonSize="medium"
                    onClick={handleClickCouponMaxSavingsPopup}
                >
                    {ctaText}
                </SuperButton>
            </ContentWrapper>
            <CloseIconContainer>
                <CloseIcon
                    dataTestId={CART_MODAL_MAX_SAVINGS_TEST_IDS.CART_MODAL_MAX_SAVINGS_CLOSE}
                    onClick={closeModal}
                    IconBgColor={CLOSE_ICON.color}
                    IconFillColor={CLOSE_ICON.fill}
                    size={CLOSE_ICON.size}
                />
            </CloseIconContainer>
        </Container>
    );
};

export default DialogHOC(React.memo(CartModalMaxSavings));
